import SEO from '../components/SEO';
import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { zones } from '../mock';

const Zones = () => {
  const zoneLinks = {
    'annecy': '/zones/annecy',
    'mont-blanc': '/zones/mont-blanc',
    'aix-chambery': '/zones/aix-chambery',
    'genevois': '/zones/genevois',
    'pays-arve': '/zones/pays-arve',
    'pays-gex': '/zones/pays-gex'
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Zones d'intervention débarras : Savoie, Haute-Savoie, Ain | DEBECO"
        description="DEBECO intervient à Annecy, Chambéry, Aix-les-Bains, Genevois, Pays de Gex et toute la Savoie pour vos débarras."
        canonical="/zones"
        jsonLd={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Zones d'intervention"}]}}
      />
      {/* Hero - Harmonized without icon */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos zones d'intervention</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Service de débarras professionnel à Annecy, dans le Genevois et en Savoie
          </p>
        </div>
      </section>

      {/* Zones Details */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {zones.map((zone, index) => {
              const linkUrl = zoneLinks[zone.id] || '/zones';
              return (
                <Card key={zone.id} className="border-2 hover:border-emerald-500 hover:shadow-2xl transition-all overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-blue-900" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white">{zone.title}</h2>
                    </div>
                  </div>
                  
                  <CardContent className="p-8">
                    <p className="text-gray-700 text-lg mb-6 leading-relaxed">{zone.description}</p>
                    
                    <div className="mb-6">
                      <h3 className="font-semibold text-blue-900 mb-3 text-lg">Villes couvertes :</h3>
                      <div className="flex flex-wrap gap-2">
                        {zone.cities.map((city, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-medium">
                            {city}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 mt-6">
                      <Link to={linkUrl} className="flex-1">
                        <Button className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white">
                          En savoir plus
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/contact" className="flex-1">
                        <Button variant="outline" className="w-full border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white">
                          Demander un devis
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { number: '48-72h', label: 'Délai d\'intervention' },
              { number: '2', label: 'Départements couverts' },
              { number: '100%', label: 'Clients satisfaits' },
              { number: '7j/7', label: 'Service disponible' }
            ].map((stat, index) => (
              <Card key={index} className="text-center border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <p className="text-4xl font-bold text-blue-900 mb-2">{stat.number}</p>
                  <p className="text-gray-600">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras dans votre secteur ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et une intervention rapide
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Zones;
